/**
 * LandingPage — reusable SEO landing page.
 *
 * Rendered at 5 URLs (see `topics.js` + App.js routes):
 *   /fried-chicken-forfar
 *   /burgers-forfar
 *   /kebabs-forfar
 *   /takeaway-delivery-forfar
 *   /desserts-forfar
 *
 * The same 5 URLs are also emitted as static HTML files by
 * `scripts/postbuild_seo.cjs` so crawlers without JS see the topic content
 * baked into the raw HTML. Both paths use the same `TOPICS` config so the
 * DOM after hydration always matches what the crawler saw.
 */
import React from 'react';
import { useLocation, Link } from 'react-router-dom';
import { Navbar } from '../../components/custom/Navbar';
import { CartSheet } from '../../components/custom/CartSheet';
import { Button } from '../../components/ui/button';
import { SEO } from '../../seo/SEO';
import { TOPICS } from './topics';
import * as meta from '../../analytics/meta';

const findTopic = (slug) => TOPICS.find((t) => t.slug === slug);

export default function LandingPage() {
  const location = useLocation();
  const slug = (location.pathname || '').replace(/^\/+|\/+$/g, '');
  const topic = findTopic(slug);

  // Fire Meta Pixel `ViewContent` once per landing view. Ads Manager
  // uses this to build lookalike audiences of Forfar-intent visitors.
  React.useEffect(() => {
    if (topic) {
      meta.track('ViewContent', {
        content_name: topic.h1,
        content_category: 'landing',
        content_ids: [topic.slug],
        currency: 'GBP',
      });
    }
  }, [topic]);

  if (!topic) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <SEO title="Page not found" noIndex includeRestaurantSchema={false} />
        <div className="text-center">
          <h1 className="text-4xl font-bold mb-2">Not found</h1>
          <p className="text-muted-foreground mb-4">This landing page is no longer available.</p>
          <Link to="/">
            <Button className="rounded-full" data-testid="landing-back-home">Back to home</Button>
          </Link>
        </div>
      </div>
    );
  }

  const breadcrumbs = [
    { name: 'Home', url: 'https://cravincrispy.co.uk/' },
    { name: topic.h1, url: `https://cravincrispy.co.uk/${topic.slug}` },
  ];

  return (
    <div className="min-h-screen bg-background">
      <SEO
        title={topic.title}
        description={topic.description}
        breadcrumbs={breadcrumbs}
        includeRestaurantSchema
      />
      <Navbar />
      <CartSheet />

      <article className="max-w-3xl mx-auto px-4 md:px-8 py-8 md:py-14">
        <header className="mb-8">
          <p className="text-sm text-primary font-semibold uppercase tracking-wider mb-2">
            Cravin Crrispy · Forfar (DD8)
          </p>
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold mb-4 leading-tight">
            {topic.h1}
          </h1>
          <p className="text-lg text-muted-foreground leading-relaxed">{topic.intro}</p>
        </header>

        <div className="space-y-8">
          {topic.sections.map((s, i) => (
            <section key={i} className="border-t pt-6">
              <h2 className="text-2xl font-bold mb-3">{s.h2}</h2>
              {Array.isArray(s.body) ? (
                <ul className="list-disc pl-5 space-y-1.5 text-base leading-relaxed">
                  {s.body.map((line, j) => (
                    <li key={j}>{line}</li>
                  ))}
                </ul>
              ) : (
                <p className="text-base leading-relaxed">{s.body}</p>
              )}
            </section>
          ))}
        </div>

        <div className="mt-10 flex flex-wrap gap-2">
          {topic.focusKeywords.map((k) => (
            <span
              key={k}
              className="text-xs bg-secondary text-secondary-foreground px-3 py-1.5 rounded-full"
            >
              {k}
            </span>
          ))}
        </div>

        <div className="mt-10 border-t pt-8 flex flex-col sm:flex-row gap-4">
          <Link to="/">
            <Button
              className="rounded-full h-12 px-8 text-base"
              data-testid={`landing-cta-${topic.slug}`}
            >
              {topic.ctaLabel}
            </Button>
          </Link>
          <a
            href="tel:+441307477786"
            onClick={() => meta.track('Contact', { content_category: 'phone_call' })}
            data-testid={`landing-call-${topic.slug}`}
          >
            <Button variant="outline" className="rounded-full h-12 px-8 text-base">
              Call 01307 477786
            </Button>
          </a>
        </div>

        <address className="mt-10 not-italic text-sm text-muted-foreground border-t pt-6">
          <strong className="text-foreground">Cravin Crrispy</strong>
          <br />
          3-5 Queen St, Forfar, DD8 3AJ
          <br />
          Open daily 16:00 – 23:00 · Preorders from 11:00
        </address>
      </article>
    </div>
  );
}
